import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Card,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  Typography as Typo,
  Box as MuiBox,
  Tabs,
  Tab,
  Divider,
  IconButton,
  LinearProgress,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { Scanner } from '@yudiel/react-qr-scanner';
import axios from 'axios';
import api from '../../utils/functions/api';
import { preprocessDocumentImage } from '../../utils/imagePreprocess';
import * as actions from '../../redux/actions';
import { BackButton } from '../../components/Button';
import { EmployeeForm } from '../../components/Forms';
import { Loading } from '../../components/Loading';
import { Error } from '../../components/Error';

// Thai ID-1: 85.6mm × 54mm at 300 DPI → 1011×638 px (85.6×300/25.4 = 1011.02)
const ID_TW = 1011;
const ID_TH = 638;
const ID_TR = ID_TW / ID_TH; // ≈ 1.5846

function cropToIdCard(dataUrl, bbox) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      let sx; let sy; let sw; let sh;

      const validBbox = Array.isArray(bbox) && bbox.length === 4
        && bbox.every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1)
        && bbox[2] > bbox[0] && bbox[3] > bbox[1];

      if (validBbox) {
        const [x0, y0, x1, y1] = bbox;
        sx = x0 * W; sy = y0 * H;
        sw = (x1 - x0) * W; sh = (y1 - y0) * H;
        // Expand shorter dimension to match ID card ratio (keep center)
        const r = sw / sh;
        if (r > ID_TR) {
          const newH = sw / ID_TR;
          sy -= (newH - sh) / 2;
          sh = newH;
        } else {
          const newW = sh * ID_TR;
          sx -= (newW - sw) / 2;
          sw = newW;
        }
      } else {
        // No bbox — center crop to ID card ratio
        const r = W / H;
        if (r > ID_TR) {
          sw = H * ID_TR; sh = H; sx = (W - sw) / 2; sy = 0;
        } else {
          sw = W; sh = W / ID_TR; sx = 0; sy = (H - sh) / 2;
        }
      }

      // Slide before cut: shift viewport inward before truncating
      if (sx < 0) sx = 0;
      if (sy < 0) sy = 0;
      if (sx + sw > W) sx = Math.max(0, W - sw);
      if (sy + sh > H) sy = Math.max(0, H - sh);
      // Final clamp in case image is smaller than expanded region
      sw = Math.min(sw, W - sx);
      sh = Math.min(sh, H - sy);

      const canvas = document.createElement('canvas');
      canvas.width = ID_TW; canvas.height = ID_TH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, ID_TW, ID_TH);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, ID_TW, ID_TH);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.src = dataUrl;
  });
}

const FIELD_LABELS = {
  firstname: 'ชื่อ (EN)',
  lastname: 'นามสกุล (EN)',
  firstname_th: 'ชื่อ (TH)',
  lastname_th: 'นามสกุล (TH)',
  title: 'คำนำหน้า',
  phone_number: 'เบอร์โทร',
  tax_id: 'เลขบัตรประชาชน',
  'salary.month': 'เงินเดือน (รายเดือน)',
  'salary_extra.month': 'เงินเพิ่มพิเศษ',
  department: 'แผนก',
  role: 'ตำแหน่ง',
  type: 'ประเภทพนักงาน',
  work_status: 'สถานะการทำงาน',
  date_start: 'วันที่เริ่มงาน',
  date_resign: 'วันที่ลาออก',
  gender: 'เพศ',
  date_of_birth: 'วันเกิด',
  religion: 'ศาสนา',
  marital_status: 'สถานะการสมรส',
  children_count: 'จำนวนบุตร',
  'field_allowance.enabled': 'ค่าออกสนาม (เปิด/ปิด)',
  'field_allowance.override_amount': 'ค่าออกสนามพิเศษ',
  'permissions.admin': 'สิทธิ์ Admin',
  'permissions.hr_management': 'สิทธิ์ HR Management',
  'permissions.hr_report': 'สิทธิ์ HR Report',
  'permissions.hr_edit': 'สิทธิ์ HR Edit',
  'permissions.finance_approve': 'สิทธิ์ Finance Approve',
  'bank_account.number': 'เลขบัญชี',
  'bank_account.account_name': 'ชื่อบัญชี',
  'bank_account.bank_name': 'ธนาคาร',
  'bank_account.account_type': 'ประเภทบัญชี',
  'emergency_contact.name': 'ผู้ติดต่อฉุกเฉิน',
  'emergency_contact.phone': 'เบอร์ผู้ติดต่อฉุกเฉิน',
  'emergency_contact.relationship': 'ความสัมพันธ์',
  'address.house_number': 'บ้านเลขที่',
  'address.village_number': 'หมู่',
  'address.road': 'ถนน',
  'address.subdistrict': 'ตำบล/แขวง',
  'address.district': 'อำเภอ/เขต',
  'address.province': 'จังหวัด',
  timestamp_use_location: 'ใช้ GPS ลงเวลา',
  project_access_mode: 'โหมดเข้าถึงโปรเจกต์',
  assigned_projects: 'โปรเจกต์ที่รับผิดชอบ',
};

function formatAuditValue(val) {
  if (val === null || val === undefined || val === '') return '(ไม่มี)';
  if (val === 'true' || val === true) return 'เปิด';
  if (val === 'false' || val === false) return 'ปิด';
  return String(val);
}

const DOC_CATEGORIES = [
  { key: 'id_card', label: 'บัตรประชาชน' },
  { key: 'house_registration', label: 'ทะเบียนบ้าน' },
  { key: 'education', label: 'วุฒิการศึกษา' },
  { key: 'resume', label: 'Resume / CV' },
  { key: 'certificate', label: 'ใบรับรอง / ใบอบรม' },
  { key: 'other', label: 'เอกสารอื่นๆ' },
];

const qrStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  p: 4,
};

function DocCard({ url, label, onDelete }) {
  const [preview, setPreview] = useState(false);
  const isImage = /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);

  const handleClick = () => {
    if (isImage) setPreview(true);
    else window.open(url, '_blank');
  };

  return (
    <>
      <MuiBox
        sx={{
          position: 'relative',
          width: 120,
          height: 90,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid #ddd',
          cursor: 'pointer',
          flexShrink: 0,
          '&:hover': { boxShadow: 2 },
        }}
        onClick={handleClick}
      >
        {isImage ? (
          <img src={url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <MuiBox
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f5f5f5',
              gap: 0.5,
            }}
          >
            <InsertDriveFileIcon color="action" />
            <Typo variant="caption" sx={{ px: 0.5, textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.2 }}>
              {label}
            </Typo>
          </MuiBox>
        )}
        {onDelete && (
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
              p: 0.3,
            }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        )}
        <MuiBox
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            px: 0.5,
            py: 0.2,
          }}
        >
          <Typo variant="caption" color="white" noWrap display="block">
            {label}
          </Typo>
        </MuiBox>
      </MuiBox>

      <Dialog open={preview} onClose={() => setPreview(false)} maxWidth="lg" PaperProps={{ sx: { bgcolor: '#111' } }}>
        <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <img src={url} alt={label} style={{ maxWidth: '88vw', maxHeight: '78vh', display: 'block', objectFit: 'contain', borderRadius: 4 }} />
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#111', py: 0.5 }}>
          <Typo variant="caption" color="grey.400" sx={{ flex: 1, ml: 1 }}>{label}</Typo>
          <Button size="small" onClick={() => window.open(url, '_blank')} sx={{ color: 'grey.300' }}>เปิดในแท็บใหม่</Button>
          <Button size="small" onClick={() => setPreview(false)} sx={{ color: 'grey.400' }}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DocCard.propTypes = {
  url: PropTypes.string.isRequired,
  label: PropTypes.string,
  onDelete: PropTypes.func,
};

DocCard.defaultProps = {
  label: '',
  onDelete: null,
};

export default function HrEditEmployee({ title, subtitle }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [employeeImage, setEmployeeImage] = useState([]);
  const employee = useSelector((state) => state.employee);
  const roletype = useSelector((state) => state.roletype);
  const department = useSelector((state) => state.department);
  const role = useSelector((state) => state.role);
  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      firstname: employee.firstname,
      lastname: employee.lastname,
      department: department.id,
      role: roletype.id,
      phone_number: employee.phone_number,
      check_key: employee?.check_key,
    },
  });

  const [openAddAccount, setOpenAddAccount] = React.useState(false);
  const [accountForm, setAccountForm] = React.useState({ password: '', confirmPassword: '' });
  const [accountInfo, setAccountInfo] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [docUploading, setDocUploading] = useState(false);
  const docFileRefs = useRef({});

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // --- ID Card Scanner state ---
  const [openIdScan, setOpenIdScan] = useState(false);
  const [idScanImage, setIdScanImage] = useState(null);
  const [idScanMime, setIdScanMime] = useState('image/jpeg');
  const [idScanPreviewUrl, setIdScanPreviewUrl] = useState(null);
  const [idScanLoading, setIdScanLoading] = useState(false);
  const [idScanResult, setIdScanResult] = useState(null);
  const [idScanError, setIdScanError] = useState('');
  const [idScanDuplicates, setIdScanDuplicates] = useState([]);
  const [idScanDupLoading, setIdScanDupLoading] = useState(false);
  const idFileRef = useRef();
  const idCardReplaceRef = useRef();
  const [idCardPreviewOpen, setIdCardPreviewOpen] = useState(false);

  const runDuplicateCheck = async (result) => {
    if (!result) return;
    setIdScanDupLoading(true);
    setIdScanDuplicates([]);
    try {
      const terms = _.uniq([result.tax_id, result.firstname_th, result.firstname].filter(Boolean));
      const found = [];
      for (const term of terms) {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/employee?name=${encodeURIComponent(term)}&size=20&page=1&activeOnly=false`,
        );
        (data?.rows || []).forEach((r) => {
          if (String(r._id) === String(id)) return;
          const taxMatch = result.tax_id && r.tax_id === result.tax_id;
          const nameThMatch = result.firstname_th && result.lastname_th
            && r.firstname_th === result.firstname_th && r.lastname_th === result.lastname_th;
          const nameEnMatch = result.firstname && result.lastname
            && r.firstname?.toLowerCase() === result.firstname?.toLowerCase()
            && r.lastname?.toLowerCase() === result.lastname?.toLowerCase();
          if ((taxMatch || nameThMatch || nameEnMatch) && !found.find((f) => f.employee._id === r._id)) {
            found.push({ employee: r, reason: taxMatch ? 'เลขบัตรประชาชนซ้ำ' : 'ชื่อ-นามสกุลซ้ำ' });
          }
        });
      }
      setIdScanDuplicates(found);
    } catch (err) {
      console.error('Duplicate check failed:', err);
    } finally {
      setIdScanDupLoading(false);
    }
  };

  const handleIdImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIdScanResult(null);
    setIdScanError('');
    setIdScanMime(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setIdScanPreviewUrl(dataUrl);
      setIdScanImage(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleIdScanAnalyze = async () => {
    if (!idScanImage) return;
    setIdScanLoading(true);
    setIdScanError('');
    setIdScanResult(null);
    try {
      const base64 = await preprocessDocumentImage(`data:${idScanMime};base64,${idScanImage}`);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/ai/scan-id-card`,
        { image: base64, mimeType: 'image/jpeg' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const result = data?.result ?? data;
      setIdScanResult(result);
      await runDuplicateCheck(result);
    } catch (err) {
      setIdScanError(err?.response?.data?.error?.message || 'วิเคราะห์ไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setIdScanLoading(false);
    }
  };

  const resetIdScanState = () => {
    setOpenIdScan(false);
    setIdScanImage(null);
    setIdScanPreviewUrl(null);
    setIdScanResult(null);
    setIdScanError('');
  };

  const saveIdCardImage = (previewUrl, bbox) => {
    cropToIdCard(previewUrl, bbox).then((croppedUrl) => {
      axios.put(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
        { id_card_image: { image: croppedUrl, imageType: 'profile', alt: 'id_card' } },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      ).then(() => dispatch(actions.employeeGet(id)))
        .catch((e) => console.error('id_card_image save failed', e));
    });
  };

  const handleIdScanFill = async () => {
    if (!idScanResult) return;
    const r = idScanResult;
    if (r.title) setValue('title', r.title);
    if (r.firstname) setValue('firstname', r.firstname);
    if (r.lastname) setValue('lastname', r.lastname);
    if (r.firstname_th) setValue('firstname_th', r.firstname_th);
    if (r.lastname_th) setValue('lastname_th', r.lastname_th);
    if (r.tax_id) setValue('tax_id', r.tax_id);
    if (r.date_of_birth) setValue('date_of_birth', r.date_of_birth);
    if (r.gender) setValue('gender', r.gender);
    if (r.religion) setValue('religion', r.religion);
    else setValue('religion', 'ไม่ระบุ');
    if (r.address) {
      if (r.address.house_number) setValue('address.house_number', r.address.house_number);
      if (r.address.village_number) setValue('address.village_number', r.address.village_number);
      if (r.address.road) setValue('address.road', r.address.road);
      if (r.address.subdistrict) setValue('address.subdistrict', r.address.subdistrict);
      if (r.address.district) setValue('address.district', r.address.district);
      if (r.address.province) setValue('address.province', r.address.province);
    }
    if (idScanPreviewUrl) saveIdCardImage(idScanPreviewUrl, r.card_bbox);
    resetIdScanState();
  };

  const handleIdScanSaveOnly = () => {
    if (!idScanPreviewUrl) return;
    saveIdCardImage(idScanPreviewUrl, idScanResult?.card_bbox);
    resetIdScanState();
  };

  // --- Document handlers ---
  const handleDocUpload = async (category, fileName, file) => {
    setDocUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = (e) => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await axios.post(
        `${process.env.REACT_APP_API_URL}/employee/${id}/documents`,
        { category, label: fileName, image: dataUrl },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      await dispatch(actions.employeeGet(id));
    } catch (e) {
      alert('อัปโหลดไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setDocUploading(false);
    }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('ลบเอกสารนี้?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/employee/${id}/documents/${docId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      await dispatch(actions.employeeGet(id));
    } catch (e) {
      alert('ลบไม่สำเร็จ');
    }
  };

  const handleIdCardImageDelete = async () => {
    if (!window.confirm('ลบรูปบัตรประชาชน?')) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
        { id_card_image: null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      await dispatch(actions.employeeGet(id));
    } catch (e) {
      alert('ลบไม่สำเร็จ');
    }
  };

  const handleIdCardReplace = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      cropToIdCard(dataUrl, null).then((croppedUrl) => {
        axios.put(
          `${process.env.REACT_APP_API_URL}/employee/${id}`,
          { id_card_image: { image: croppedUrl, imageType: 'profile', alt: 'id_card' } },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
        ).then(() => dispatch(actions.employeeGet(id)))
          .catch(() => alert('บันทึกรูปไม่สำเร็จ'));
      });
    };
    reader.readAsDataURL(file);
  };

  const openIdScanDialog = () => {
    setOpenIdScan(true);
    setIdScanImage(null);
    setIdScanPreviewUrl(null);
    setIdScanResult(null);
    setIdScanError('');
  };

  const renderIdScanDialog = () => (
    <Dialog open={openIdScan} onClose={() => setOpenIdScan(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <BadgeIcon color="primary" />
          <span>สแกนบัตรประชาชน</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <input
            ref={idFileRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleIdImageChange}
          />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => idFileRef.current?.click()}
          >
            เลือกรูปภาพ / ถ่ายรูปบัตร
          </Button>

          {idScanPreviewUrl && (
            <MuiBox sx={{ textAlign: 'center' }}>
              <img
                src={idScanPreviewUrl}
                alt="id-card-preview"
                style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </MuiBox>
          )}

          {idScanError && <Alert severity="error">{idScanError}</Alert>}

          {idScanResult && (
            <Alert severity="success">
              <Typo variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>ข้อมูลที่อ่านได้</Typo>
              {idScanResult.title && <div>คำนำหน้า: <b>{idScanResult.title}</b></div>}
              {idScanResult.firstname_th && <div>ชื่อ (TH): <b>{idScanResult.firstname_th} {idScanResult.lastname_th}</b></div>}
              {idScanResult.firstname && <div>ชื่อ (EN): <b>{idScanResult.firstname} {idScanResult.lastname}</b></div>}
              {idScanResult.tax_id && <div>เลขบัตร: <b>{idScanResult.tax_id}</b></div>}
              {idScanResult.date_of_birth && <div>วันเกิด: <b>{idScanResult.date_of_birth}</b></div>}
              {idScanResult.gender && <div>เพศ: <b>{idScanResult.gender}</b></div>}
              {idScanResult.religion && <div>ศาสนา: <b>{idScanResult.religion}</b></div>}
              {idScanResult.address?.province && <div>จังหวัด: <b>{idScanResult.address.province}</b></div>}
            </Alert>
          )}
          {idScanDupLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typo variant="caption" color="text.secondary">กำลังตรวจสอบข้อมูลซ้ำในระบบ...</Typo>
            </Stack>
          )}
          {idScanDuplicates.length > 0 && (
            <Alert severity="warning">
              <Typo variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                ⚠️ พบข้อมูลที่อาจซ้ำกันในระบบ ({idScanDuplicates.length} รายการ)
              </Typo>
              {idScanDuplicates.map((d, i) => (
                <div key={i}>
                  <b>{d.reason}</b>: {d.employee.firstname} {d.employee.lastname}
                  {d.employee.firstname_th && ` (${d.employee.firstname_th} ${d.employee.lastname_th})`}
                  {d.employee.employee_id && ` — ${d.employee.employee_id}`}
                </div>
              ))}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {idScanResult ? (
          <>
            <Button variant="contained" startIcon={<BadgeIcon />} onClick={handleIdScanFill}>
              กรอกข้อมูล + บันทึกรูป
            </Button>
            <Button variant="outlined" onClick={handleIdScanSaveOnly}>
              บันทึกรูปอย่างเดียว
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={handleIdScanAnalyze}
              disabled={!idScanImage || idScanLoading}
              startIcon={idScanLoading ? <CircularProgress size={16} color="inherit" /> : <BadgeIcon />}
            >
              {idScanLoading ? 'กำลังวิเคราะห์...' : 'วิเคราะห์'}
            </Button>
            {idScanImage && !idScanLoading && (
              <Button variant="outlined" onClick={handleIdScanSaveOnly}>
                บันทึกรูปอย่างเดียว
              </Button>
            )}
          </>
        )}
        <Button onClick={resetIdScanState}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );

  const renderDocumentsTab = () => (
    <Stack spacing={3} sx={{ pt: 2 }}>
      {docUploading && <LinearProgress />}
      {DOC_CATEGORIES.map(({ key, label }) => {
        const catDocs = (employee.documents || []).filter((d) => d.category === key);
        const isIdCard = key === 'id_card';
        return (
          <MuiBox key={key}>
            <Typo variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
              {label}
            </Typo>
            <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
              {/* รูปบัตรจาก id_card_image field (สแกนแล้ว) */}
              {isIdCard && employee.id_card_image?.url && (
                <MuiBox sx={{
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.5, overflow: 'hidden', width: 160, flexShrink: 0,
                }}>
                  <MuiBox
                    sx={{ width: '100%', height: 100, cursor: 'pointer', overflow: 'hidden' }}
                    onClick={() => setIdCardPreviewOpen(true)}
                  >
                    <img
                      src={employee.id_card_image.url}
                      alt="บัตรประชาชน"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </MuiBox>
                  <Dialog open={idCardPreviewOpen} onClose={() => setIdCardPreviewOpen(false)} maxWidth="lg" PaperProps={{ sx: { bgcolor: '#111' } }}>
                    <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <img src={employee.id_card_image.url} alt="บัตรประชาชน" style={{ maxWidth: '88vw', maxHeight: '78vh', display: 'block', objectFit: 'contain', borderRadius: 4 }} />
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: '#111', py: 0.5 }}>
                      <Typo variant="caption" color="grey.400" sx={{ flex: 1, ml: 1 }}>บัตรประชาชน</Typo>
                      <Button size="small" onClick={() => window.open(employee.id_card_image.url, '_blank')} sx={{ color: 'grey.300' }}>เปิดในแท็บใหม่</Button>
                      <Button size="small" onClick={() => setIdCardPreviewOpen(false)} sx={{ color: 'grey.400' }}>ปิด</Button>
                    </DialogActions>
                  </Dialog>
                  <MuiBox sx={{ bgcolor: 'grey.50', px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typo variant="caption" color="text.secondary" noWrap display="block" sx={{ mb: 0.5 }}>
                      บัตรประชาชน
                    </Typo>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small" color="error" title="ลบรูปบัตร"
                        onClick={handleIdCardImageDelete}
                        sx={{ p: 0.4, border: '1px solid', borderColor: 'error.light', borderRadius: 1 }}
                      >
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small" color="primary" title="เปลี่ยนรูป"
                        onClick={() => idCardReplaceRef.current?.click()}
                        sx={{ p: 0.4, border: '1px solid', borderColor: 'primary.light', borderRadius: 1 }}
                      >
                        <UploadFileIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small" color="primary" title="สแกนใหม่ด้วย AI"
                        onClick={openIdScanDialog}
                        sx={{ p: 0.4, border: '1px solid', borderColor: 'primary.light', borderRadius: 1 }}
                      >
                        <BadgeIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Stack>
                  </MuiBox>
                  <input
                    ref={idCardReplaceRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handleIdCardReplace}
                  />
                </MuiBox>
              )}
              {/* เอกสารใน documents array */}
              {catDocs.map((doc) => (
                <DocCard
                  key={doc._id}
                  url={doc.file_url}
                  label={doc.label}
                  onDelete={() => handleDocDelete(doc._id)}
                />
              ))}
              {/* ปุ่มสแกน AI (เฉพาะหมวดบัตรประชาชน) */}
              {isIdCard && (
                <MuiBox
                  sx={{
                    width: 120,
                    height: 90,
                    border: '2px dashed',
                    borderColor: 'primary.light',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: docUploading ? 'not-allowed' : 'pointer',
                    gap: 0.5,
                    color: 'primary.main',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  }}
                  onClick={() => !docUploading && openIdScanDialog()}
                >
                  <BadgeIcon fontSize="small" />
                  <Typo variant="caption">สแกน AI</Typo>
                </MuiBox>
              )}
              {/* ปุ่ม upload ไฟล์ */}
              <MuiBox
                sx={{
                  width: 120,
                  height: 90,
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: docUploading ? 'not-allowed' : 'pointer',
                  gap: 0.5,
                  color: 'text.secondary',
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                }}
                onClick={() => !docUploading && docFileRefs.current[key]?.click()}
              >
                <UploadFileIcon fontSize="small" />
                <Typo variant="caption">อัปโหลดไฟล์</Typo>
              </MuiBox>
              <input
                ref={(el) => { docFileRefs.current[key] = el; }}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleDocUpload(key, file.name, file);
                  e.target.value = '';
                }}
              />
            </MuiBox>
            <Divider sx={{ mt: 2 }} />
          </MuiBox>
        );
      })}
    </Stack>
  );

  const renderAuditLogTab = () => (
    <Stack spacing={0} sx={{ pt: 2 }}>
      {auditLoading && <LinearProgress sx={{ mb: 2 }} />}
      {!auditLoading && auditLogs.length === 0 && (
        <Typo variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          ยังไม่มีประวัติการแก้ไข
        </Typo>
      )}
      {auditLogs.map((log) => {
        const dt = new Date(log.createdAt).toLocaleString('th-TH', {
          timeZone: 'Asia/Bangkok',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        return (
          <MuiBox
            key={log._id}
            sx={{
              borderLeft: '3px solid',
              borderColor: 'primary.light',
              pl: 2,
              py: 1.5,
              mb: 2,
              bgcolor: 'grey.50',
              borderRadius: '0 6px 6px 0',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <HistoryIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typo variant="caption" color="text.secondary">{dt}</Typo>
              {log.changed_by?.display_name && (
                <Chip
                  label={log.changed_by.display_name}
                  size="small"
                  sx={{ height: 18, fontSize: 10 }}
                />
              )}
            </Stack>
            {(log.changes || []).map((ch, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ mt: 0.5, flexWrap: 'wrap' }}
              >
                <Typo
                  variant="caption"
                  sx={{ minWidth: 160, color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}
                >
                  {FIELD_LABELS[ch.field] || ch.field}
                </Typo>
                <Typo variant="caption" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                  {formatAuditValue(ch.old_value)}
                </Typo>
                <Typo variant="caption" color="text.secondary">→</Typo>
                <Typo variant="caption" sx={{ color: 'success.dark', fontWeight: 600 }}>
                  {formatAuditValue(ch.new_value)}
                </Typo>
              </Stack>
            ))}
          </MuiBox>
        );
      })}
    </Stack>
  );

  const handleOpenAccountDialog = async () => {
    if (employee?.uid) {
      try {
        const info = await dispatch(actions.userGetByEmployeeId(id));
        setAccountInfo(info);
        setAccountForm({ password: '', confirmPassword: '' });
      } catch (e) {
        setAccountInfo(null);
      }
    } else {
      setAccountInfo(null);
      setAccountForm({ username: '', password: '', confirmPassword: '' });
    }
    setOpenAddAccount(true);
  };

  const [open, setOpen] = React.useState(false);
  const [qr, setQr] = React.useState(employee?.check_key);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    dispatch(actions.employeeGet(id));
    dispatch(actions.roletypeAll(''));
    dispatch(actions.departmentAll({}));
    dispatch(actions.roleAll());
    return () => {};
  }, []);

  useEffect(() => {
    if (employee?.system_role) {
      const ids = (employee.system_role).map((r) => r?.id || r?._id || String(r)).filter(Boolean);
      setValue('system_role', ids);
    }
  }, [employee?.system_role]);

  useEffect(() => {
    if (activeTab !== 2) return;
    setAuditLoading(true);
    api.get(`${process.env.REACT_APP_API_URL}/employee/${id}/audit-log`)
      .then(({ data }) => setAuditLogs(Array.isArray(data) ? data : []))
      .catch(() => setAuditLogs([]))
      .finally(() => setAuditLoading(false));
  }, [activeTab, id]);

  useEffect(() => {
    setQr(employee?.check_key);
    return () => {};
  }, [employee]);

  const handleScan = async (data) => {
    setQr(data);
    setOpen(false);
    setValue('check_key', data);
  };

  const handleAddAccount = async () => {
    if (accountForm.password !== accountForm.confirmPassword) {
      alert('รหัสผ่านไม่ตรงกัน');
      return;
    }
    try {
      if (employee?.uid) {
        await dispatch(actions.userPut(employee?.uid?._id || employee?.uid, { password: accountForm.password }));
        alert('เปลี่ยนรหัสผ่านสำเร็จ');
      } else {
        await dispatch(actions.userLinkToEmployee(id, {
          username: accountForm.username,
          password: accountForm.password,
        }));
        await dispatch(actions.employeeGet(id));
        alert('สร้างบัญชีผู้ใช้สำเร็จ');
      }
      setOpenAddAccount(false);
      setAccountForm({ username: '', password: '', confirmPassword: '' });
    } catch (error) {
      alert(error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const renderAddAccountDialog = () => (
    <Dialog open={openAddAccount} onClose={() => setOpenAddAccount(false)} maxWidth="xs" fullWidth>
      {employee?.uid ? (
        <>
          <DialogTitle>บัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-3 pt-2">
              <TextField
                label="Username"
                fullWidth
                value={accountInfo?.username || ''}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
              />
              <TextField
                label="รหัสผ่านใหม่"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={accountForm.password}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, password: e.target.value }))}
              />
              <TextField
                label="ยืนยันรหัสผ่านใหม่"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              />
              <Button
                size="small"
                onClick={() => setShowPassword((prev) => !prev)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', p: 0 }}
              >
                {showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              </Button>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddAccount} variant="contained">เปลี่ยนรหัสผ่าน</Button>
            <Button onClick={() => setOpenAddAccount(false)} variant="outlined">ปิด</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>เพิ่มบัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-3 pt-2">
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountForm.username || ''}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, username: e.target.value }))}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, password: e.target.value }))}
              />
              <TextField
                label="ยืนยัน Password"
                type="password"
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddAccount} variant="contained">สร้างบัญชี</Button>
            <Button onClick={() => setOpenAddAccount(false)} variant="outlined">ยกเลิก</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderQrCode = () => (
    <div>
      <Modal open={open} onClose={handleClose}>
        <Box sx={qrStyle}>
          <Typography variant="h6" component="h2">scan qr code</Typography>
          <Typography sx={{ mt: 2 }}>
            <Scanner
              onScan={(results) => handleScan(results?.[0]?.rawValue)}
              allowMultiple
            />
          </Typography>
        </Box>
      </Modal>
    </div>
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };

      if (!_.isEmpty(employeeImage)) {
        payload.image = {
          image: employeeImage[0]?.data_url,
          imageType: 'profile',
          alt: '',
        };
      }

      await dispatch(actions.employeePut(id, payload));
      await dispatch(actions.employeeGet(id));
      setEmployeeImage([]);
      alert('สำเร็จ');
    } catch (error) {
      console.log(error);
    }
  };

  if (employee.isLoading || employee.rows) {
    return <Loading />;
  }
  if (!employee.isLoading && employee.isCompleted) {
    return (
      <div>
        {renderQrCode()}
        {renderAddAccountDialog()}
        {renderIdScanDialog()}
        <div className="flex flex-row justify-between pb-4 items-center">
          <div>
            <BackButton />
          </div>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<BadgeIcon />}
            onClick={openIdScanDialog}
          >
            สแกนบัตรประชาชน
          </Button>
        </div>
        <Card className="p-6">
          <MuiBox sx={{ bgcolor: 'grey.100', mx: -3, px: 3, pt: 1, mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              TabIndicatorProps={{ style: { height: 3, borderRadius: '3px 3px 0 0' } }}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  minHeight: 44,
                  borderRadius: '6px 6px 0 0',
                  color: 'text.secondary',
                  mr: 0.5,
                  '&.Mui-selected': { color: 'primary.main', fontWeight: 700, bgcolor: 'background.paper' },
                },
              }}
            >
              <Tab label="ข้อมูลทั่วไป" />
              <Tab
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>เอกสาร</span>
                    {(employee.documents?.length > 0 || employee.id_card_image) && (
                      <Chip
                        label={(employee.documents?.length || 0) + (employee.id_card_image ? 1 : 0)}
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                  </Stack>
                }
              />
              <Tab
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <HistoryIcon sx={{ fontSize: 15 }} />
                    <span>ประวัติการแก้ไข</span>
                  </Stack>
                }
              />
            </Tabs>
          </MuiBox>

          {activeTab === 0 && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmployeeForm
                control={control}
                Controller={Controller}
                errors={errors}
                employee={employee}
                roletype={roletype}
                role={role}
                employeeImage={employeeImage}
                setEmployeeImage={setEmployeeImage}
                department={department}
                handleOpen={handleOpen}
                qr={qr}
                watch={watch}
                setValue={setValue}
              />
              <div className="flex flex-row justify-between gap-1 py-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenAccountDialog}
                  >
                    {employee?.uid ? 'จัดการบัญชีผู้ใช้' : 'สร้างบัญชีผู้ใช้'}
                  </Button>
                  <Chip
                    label={employee?.uid ? 'มีบัญชีแล้ว' : 'ยังไม่มีบัญชี'}
                    color={employee?.uid ? 'success' : 'default'}
                    size="small"
                  />
                </div>
                <div className="flex-1" />
                <Button variant="contained" type="submit">
                  บันทึก
                </Button>
              </div>
            </form>
          )}

          {activeTab === 1 && renderDocumentsTab()}
          {activeTab === 2 && renderAuditLogTab()}
        </Card>
      </div>
    );
  }
  return <Error />;
}

HrEditEmployee.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

HrEditEmployee.defaultProps = {
  title: '',
  subtitle: '',
};
